import { useAuth0 } from '@auth0/auth0-react';
import { ArrowPathIcon, InformationCircleIcon } from '@heroicons/react/20/solid';
import { useRouter } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';

function useUpdateChecker() {
  const [isOutdated, setIsOutdated] = useState(false);
  // check for update every 5 minutes
  const checkVersion = useCallback(() => {
    // skip version check in development
    if (import.meta.env.MODE !== 'production') {
      return;
    }

    fetch('/assets/VERSION', { cache: 'no-store' })
      .then((response) => response.text())
      .then((version) => {
        const trimmedVersion = version.trim();
        if (import.meta.env.VITE_APP_VERSION !== trimmedVersion) {
          setIsOutdated(true);
        }
      })
      .catch((error) => {
        console.warn('Error checking version:', error);
      });
  }, []);

  // initial version check + poll every 5 minutes
  useEffect(() => {
    checkVersion();
    const intervalId = setInterval(checkVersion, 300000); // 5 * 60 * 1000

    return () => clearInterval(intervalId);
  }, [checkVersion]);

  return isOutdated;
}

/**
 * Once a newer build is deployed, turn the next route change into a document
 * load so the tab picks it up.
 *
 * A route change is already a teardown boundary — the page being left is
 * unmounting either way — which is what makes this safe without tracking
 * unsaved work anywhere. Reloading a tab *while* the owner is on a page is
 * what would cost them something, and that never happens here.
 *
 * Only on a path change. Table filters, report ranges and pagination all write
 * to the query string as the owner works, and reloading on those would restart
 * the page under them mid-task.
 */
function useReloadOnNavigate(isOutdated: boolean) {
  const router = useRouter();

  useEffect(() => {
    if (!isOutdated) {
      return;
    }

    return router.subscribe('onBeforeNavigate', ({ toLocation, pathChanged }) => {
      if (!pathChanged) {
        return;
      }

      // assign, not reload: the destination is where the owner asked to go,
      // and the client-side transition to it is superseded by this.
      window.location.assign(toLocation.href);
    });
  }, [isOutdated, router]);
}

export function Updater() {
  const isOutdated = useUpdateChecker();
  const { isAuthenticated } = useAuth0();

  // Not gated on isAuthenticated: the banner needs somewhere to show, this
  // doesn't.
  useReloadOnNavigate(isOutdated);
  const [isUpdating, setIsUpdating] = useState(false);

  if (isAuthenticated && isOutdated) {
    const handleUpdate = () => {
      setIsUpdating(true);
      // reload doesn't work unless in a setTimeout
      // https://stackoverflow.com/questions/18967532/window-location-reload-not-working-for-firefox-and-chrome
      setTimeout(() => window.location.reload(), 100);
    };

    return (
      <div className="rounded-md bg-blue-50 p-4 flex gap-2 items-center fixed top-0 right-0 m-2">
        <InformationCircleIcon aria-hidden="true" className="h-5 w-5 text-blue-400" />
        <p className="text-sm text-blue-700">
          A new software update is available. Please refresh the page to get the latest version.
        </p>
        <a
          href={`${import.meta.env.VITE_MARKETING_URL}/changelog`}
          className="whitespace-nowrap font-medium text-blue-700 hover:text-blue-600 hover:bg-blue-100 px-2 py-1 rounded-md"
        >
          See what's new
        </a>
        <button
          className="whitespace-nowrap font-medium text-blue-700 flex items-center gap-2 px-2 py-1 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:enabled:text-blue-600 hover:enabled:bg-blue-100"
          type="button"
          onClick={handleUpdate}
          disabled={isUpdating}
        >
          {isUpdating ? 'Updating…' : 'Update'}
          <ArrowPathIcon
            aria-hidden="true"
            className={`h-5 w-5 text-blue-400 ${isUpdating ? 'animate-spin' : ''}`}
          />
        </button>
      </div>
    );
  }
}
